import React, { useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ChangePasswordDialog({ open, onOpenChange }) {
  const { changePassword, formatApiError } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setCurrent(""); setNext(""); setConfirm(""); setError(""); };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (next !== confirm) { setError("New passwords do not match."); return; }
    if (next.length < 6) { setError("New password must be at least 6 characters."); return; }
    setLoading(true);
    try {
      await changePassword(current, next);
      toast.success("Password updated");
      reset();
      onOpenChange(false);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="bg-slate-900 border-slate-800 text-slate-100 max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Change password</DialogTitle>
          <DialogDescription className="text-slate-400">Enter your current password and choose a new one.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="change-password-form">
          <div className="space-y-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Current password</Label>
            <Input type={showPw ? "text" : "password"} required data-testid="cp-current"
              value={current} onChange={(e) => setCurrent(e.target.value)}
              className="bg-slate-950 border-slate-800 text-slate-100 h-11" />
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">New password (min 6)</Label>
            <div className="relative">
              <Input type={showPw ? "text" : "password"} required minLength={6} data-testid="cp-new"
                value={next} onChange={(e) => setNext(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100 h-11 pr-10" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 h-8 w-8 flex items-center justify-center">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Confirm new password</Label>
            <Input type={showPw ? "text" : "password"} required minLength={6} data-testid="cp-confirm"
              value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="bg-slate-950 border-slate-800 text-slate-100 h-11" />
          </div>
          {error && (
            <div data-testid="cp-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <DialogFooter>
            <Button type="submit" disabled={loading} data-testid="cp-submit"
              className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
